import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import PageHeader from "../layout/PageHeader";
import api from "../../api/client";
import {
  fetchAiHiringCandidates,
  getCandidatePhoneRecording,
  getCandidateRecording,
  unlockMoreAiHiringCandidates,
  updateAiHiringDecision,
} from "../../utils/api";
import { formatYyyyDdMm } from "../../utils/date";
import { VIDEO_INTERVIEW_FEATURE_ENABLED } from "../../utils/videoInterviewFeature";
import PhoneInterviewDetailsSection from "./PhoneInterviewDetailsSection";
import TranscriptMessageBubble from "../TranscriptMessageBubble";

const scoreToLabel = (score) => {
  const value = Number(score || 0);
  if (value >= 85) return { label: "Very Strong", color: "#16a34a", bg: "#dcfce7" };
  if (value >= 70) return { label: "Strong", color: "#2563eb", bg: "#dbeafe" };
  if (value >= 55) return { label: "Good", color: "#7c3aed", bg: "#ede9fe" };
  if (value >= 40) return { label: "Fair", color: "#d97706", bg: "#fef3c7" };
  if (value >= 25) return { label: "Weak", color: "#dc2626", bg: "#fee2e2" };
  return { label: "Not Recommended", color: "#6b7280", bg: "#f3f4f6" };
};

const SORT_OPTIONS = [
  { value: "date_desc", label: "Date: Newest first", sortBy: "date", order: "desc" },
  { value: "date_asc", label: "Date: Oldest first", sortBy: "date", order: "asc" },
];

const ScoreBadge = ({ score }) => {
  const { label, color, bg } = scoreToLabel(score);
  return (
    <span
      style={{
        backgroundColor: bg,
        color,
        padding: "2px 10px",
        borderRadius: "12px",
        fontWeight: 600,
        fontSize: "0.8rem",
        letterSpacing: "0.02em",
        display: "inline-block",
      }}
    >
      {label}
    </span>
  );
};

// Evaluation Criteria checklist (Agency.evaluationCriteriaEnabled) — replaces
// the numeric ScoreBadge with an "X of Y items met" summary. Candidates from
// agencies without the flag never have criteriaTotalCount set (stays 0), so
// hasCriteriaChecklist() below is the switch between the two UIs.
const hasCriteriaChecklist = (candidate) => Number(candidate?.criteriaTotalCount || 0) > 0;

const CriteriaBadge = ({ metCount, totalCount }) => {
  const total = Number(totalCount || 0);
  const met = Number(metCount || 0);
  const bg = total > 0 && met === total ? "#dcfce7" : "#dbeafe";
  const color = total > 0 && met === total ? "#16a34a" : "#2563eb";
  return (
    <span
      style={{
        backgroundColor: bg,
        color,
        padding: "2px 10px",
        borderRadius: "12px",
        fontWeight: 600,
        fontSize: "0.8rem",
        letterSpacing: "0.02em",
        display: "inline-block",
      }}
    >
      {met} of {total} items met
    </span>
  );
};

// Pre-video-interview recording-consent decline flag (2026-07-28) — surfaces
// InterviewCandidate.videoConsentStatus so the hiring team knows a candidate
// declined the recording gate without digging into logs. "pending"/
// "consented" render nothing (the normal/default path).
const VIDEO_CONSENT_DECLINE_LABELS = {
  declined_rescheduled: "🎥 Declined video — rescheduled",
  declined_switched_to_phone: "🎥 Declined video — switched to phone",
};

const VideoConsentDeclineBadge = ({ videoConsentStatus }) => {
  const label = VIDEO_CONSENT_DECLINE_LABELS[videoConsentStatus];
  if (!label) return null;
  return <span className="ai-tag ai-tag--warning">{label}</span>;
};

// Video interview end-reason flag (2026-07-28) — surfaces
// InterviewCall.endedBy (via videoInterview.endedBy) so the hiring team can
// tell a candidate's deliberate early exit apart from a normal completion
// or a technical disconnect at a glance. "candidate_confirmed" (the
// pre-existing "Finish Interview" button) and "natural_completion" render
// nothing — those are the expected/normal outcomes.
const VIDEO_ENDED_BY_LABELS = {
  candidate_ended_early: { text: "⏹ Candidate ended interview early", tone: "warning" },
  timeout: { text: "⏱ Interview timed out", tone: "neutral" },
  disconnected: { text: "⚠ Candidate disconnected", tone: "warning" },
};

const VideoEndedByBadge = ({ endedBy }) => {
  const info = VIDEO_ENDED_BY_LABELS[endedBy];
  if (!info) return null;
  return (
    <span className={`ai-tag ai-tag--${info.tone}`}>{info.text}</span>
  );
};

const CriteriaChecklist = ({ criteriaResults }) => {
  const items = Array.isArray(criteriaResults) ? criteriaResults : [];
  if (!items.length) {
    return <span className="ai-muted">No checklist items configured for this job.</span>;
  }
  return (
    <div className="ai-chip-row" style={{ flexDirection: "column", alignItems: "flex-start" }}>
      {items.map((item) => {
        const icon = item.met === true ? "✅" : item.met === false ? "❌" : "➖";
        return (
          <div key={item.criteriaId} style={{ marginBottom: 6 }}>
            <span style={{ marginRight: 6 }}>{icon}</span>
            <strong>{item.label}</strong>
            {item.evidence ? (
              <span className="ai-muted"> — {item.evidence}</span>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

const AiHiringCandidates = (props) => {
  const location = useLocation();
  const locationId = props.activeLocation?.id || props.activeLocation?._id;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [source, setSource] = useState("all");
  // "date_desc" (Newest first) is the default for this agency-wide view.
  // This is purely an opt-in query param this component sends — the
  // backend's own no-param default (score-based) is unchanged for any
  // other/future caller.
  const [sortOption, setSortOption] = useState("date_desc");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [decisionLoading, setDecisionLoading] = useState(null);
  const [expandedTranscript, setExpandedTranscript] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [jobMeta, setJobMeta] = useState(null);
  const [showMoreReason, setShowMoreReason] = useState("");
  const [showMoreLoading, setShowMoreLoading] = useState(false);
  const [showMoreError, setShowMoreError] = useState("");
  const [recordingLoading, setRecordingLoading] = useState(false);
  const [recordingData, setRecordingData] = useState(null);
  const [recordingModalOpen, setRecordingModalOpen] = useState(false);
  const [audioRecordingData, setAudioRecordingData] = useState(null);
  const [audioModalOpen, setAudioModalOpen] = useState(false);
  // Keyed by candidate._id — tracks which row's "Play" button is currently
  // fetching, so only that row shows a loading state (table can have many
  // rows; a single shared loading flag would freeze every button at once).
  const [rowRecordingLoading, setRowRecordingLoading] = useState(null);
  const limit = 50;
  const handleDownloadResume = async () => {
    if (!selected || !locationId) return;
    try {
      setDownloading(true);
      const response = await api.get(
        `/ai-hiring/candidates/${selected._id}/resume`,
        {
          params: { locationId },
          responseType: "blob",
        },
      );
      const blob = new Blob([response.data], {
        type: response.headers["content-type"] || "application/octet-stream",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download =
        selected.resumeFile?.name || `resume-${selected.name || "candidate"}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      window.alert("Resume download failed. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  const statusConfig = {
    approved: { label: "Hired", className: "ai-tag ai-tag--success" },
    rejected: { label: "Passed", className: "ai-tag ai-tag--danger" },
    pending: { label: "Resume Review", className: "ai-tag ai-tag--warning" },
    ready: { label: "Final Review", className: "ai-tag ai-tag--info" },
    follow_up: { label: "Phone Interview", className: "ai-tag ai-tag--info" },
    follow_up_video: {
      label: "Video Interview",
      className: "ai-tag ai-tag--info",
    },
    phone_passed_awaiting_review: {
      label: "Final Review",
      className: "ai-tag ai-tag--info",
    },
    // Evaluation Criteria checklist equivalent of
    // "phone_passed_awaiting_review" (Agency.evaluationCriteriaEnabled
    // agencies never get a pass/fail verdict — every completed interview
    // lands here for manual review).
    awaiting_review: {
      label: "Final Review",
      className: "ai-tag ai-tag--info",
    },
    holding: { label: "Held", className: "ai-tag ai-tag--neutral" },
  };

  const normalizeRole = (role) => {
    if (!role) return "--";
    const value = String(role).toLowerCase();
    if (value.includes("caregiver")) return "Caregiver";
    if (value.includes("nurse")) return "Nurse";
    if (value.includes("staff")) return "Staff";
    return role;
  };

  useEffect(() => {
    if (!locationId) return;
    let active = true;
    setLoading(true);
    const activeSort =
      SORT_OPTIONS.find((opt) => opt.value === sortOption) || SORT_OPTIONS[0];
    fetchAiHiringCandidates({
      locationId,
      status,
      source,
      search,
      page,
      limit,
      sortBy: activeSort.sortBy,
      order: activeSort.order,
    })
      .then((data) => {
        if (!active) return;
        setItems(Array.isArray(data.items) ? data.items : []);
        setJobMeta(data?.job || null);
        setError("");
      })
      .catch((err) => {
        if (!active) return;
        setItems([]);
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to load candidates.",
        );
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [locationId, status, source, search, page, sortOption]);

  useEffect(() => {
    setExpandedTranscript(null);
  }, [selected?._id]);

  useEffect(() => {
    if (!recordingModalOpen && !audioModalOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key !== "Escape") return;
      setRecordingModalOpen(false);
      setAudioModalOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [recordingModalOpen, audioModalOpen]);

  useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    const candidateId = params.get("candidateId");
    if (!candidateId || selected?._id === candidateId) return;
    const match = items.find((item) => String(item._id) === String(candidateId));
    if (match) setSelected(match);
  }, [location.search, items, selected?._id]);

  useEffect(() => {
    if (!selected?._id) return;
    loadRecordingForCandidate(selected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?._id]);

  const hasInterviewScore = (value) =>
    typeof value === "number" && Number.isFinite(value);
  // Mirrors AiAlbertInterview's scoring.ts isVideoPath resolution: real call
  // evidence always wins over the dashboard-rollout flag (a candidate who
  // actually completed a video interview must show as video regardless of
  // VIDEO_INTERVIEW_FEATURE_ENABLED); the flag only gates the *speculative*
  // pre-interview guess (videoInterviewRequired, before any call evidence
  // exists).
  const hasPhoneEvidence = (candidate) =>
    Boolean(candidate?.phoneInterviewCompleted) ||
    hasInterviewScore(candidate?.phoneInterview?.score);
  const hasVideoEvidence = (candidate) =>
    Boolean(candidate?.videoInterviewCompleted) ||
    hasInterviewScore(candidate?.videoInterview?.score);
  const isVideoPathFor = (candidate) =>
    hasVideoEvidence(candidate) ||
    (VIDEO_INTERVIEW_FEATURE_ENABLED &&
      !hasPhoneEvidence(candidate) &&
      candidate?.videoInterviewRequired === true);
  // Phone and video are mutually-exclusive alternate paths per candidate
  // (matches the backend gate in POST /candidates/:id/decision), NOT
  // sequential stages — a candidate only ever needs to complete whichever
  // single path actually applies to them. The previous version of this
  // check unconditionally required phone evidence even for candidates who
  // took the video-only path, which meant video candidates could never
  // reach "ready to decide" no matter how long they waited (see also
  // getStageInfo below, which surfaces this same distinction in the table).
  const canDecide = (candidate) =>
    isVideoPathFor(candidate)
      ? hasVideoEvidence(candidate)
      : hasPhoneEvidence(candidate);

  // Pipeline position — deliberately separate from Rating (aiScore-derived,
  // e.g. "Weak"/"Not Recommended") and from the outcome badge (Hired/
  // Rejected/Archived): a candidate can be a "Weak" match and still be at
  // any of these four stages. Primary source of truth is the stored
  // decisionStatus field on the candidate document, since that's the one
  // field the rest of the app (drip campaigns, phone/video follow-up jobs,
  // the backend decision gate) already advances as the candidate moves
  // through the pipeline. Real interview-completion evidence can only ever
  // move a candidate FORWARD from what decisionStatus says, never backward —
  // guards against a candidate looking stuck on "Resume Processing" if
  // decisionStatus lags behind an interview that just completed.
  const STAGE_LABELS = {
    resume: "Resume Processing",
    phone: "Phone AI Interview",
    video: "Video AI Interview",
    done: "Done",
  };
  const getStageInfo = (candidate) => {
    const decisionStage =
      candidate?.decisionStatus === "approved" ||
      candidate?.decisionStatus === "rejected" ||
      candidate?.decisionStatus === "holding"
        ? "done"
        : candidate?.decisionStatus === "follow_up_video"
          ? "video"
          : candidate?.decisionStatus === "follow_up" ||
              candidate?.decisionStatus === "phone_passed_awaiting_review" ||
              // Evaluation Criteria checklist equivalent of
              // "phone_passed_awaiting_review" (Agency.evaluationCriteriaEnabled).
              candidate?.decisionStatus === "awaiting_review"
            ? "phone"
            : "resume";
    let key = decisionStage;
    if (key !== "done") {
      if (isVideoPathFor(candidate)) key = "video";
      else if (hasPhoneEvidence(candidate)) key = "phone";
    }
    return { key, label: STAGE_LABELS[key] };
  };
  const stageClassName = (key) =>
    key === "done"
      ? "ai-tag ai-tag--neutral"
      : key === "video"
        ? "ai-tag ai-tag--info"
        : key === "phone"
          ? "ai-tag ai-tag--warning"
          : "ai-tag ai-tag--neutral";
  // The "reveal 3x hiring target" cap only makes sense when this view is
  // scoped to a single job (jobMeta populated). On the agency-wide "My
  // Candidates" list (no job filter applied), jobMeta is always null, so
  // this used to silently hard-cap the table at 3 rows with no way to see
  // more — hiding any candidate (incl. today's video interviews) ranked
  // past position 3. Only apply the cap when a real job scope exists.
  const hasJobScope = Boolean(jobMeta?._id);
  const hiringTarget = Math.max(
    Number(jobMeta?.hiringTarget || jobMeta?.hiringCount || 0) || 0,
    1,
  );
  const maxVisible = hiringTarget * 3;
  const showingMore = jobMeta?.showMoreUsed === true;
  const visibleCandidates = hasJobScope
    ? items.slice(0, showingMore ? maxVisible * 2 : maxVisible)
    : items;
  const canShowMore =
    hasJobScope && items.length > maxVisible && jobMeta?.showMoreUsed !== true;
  const showMoreReasonCount = showMoreReason.trim().length;
  const showMoreReasonReady = showMoreReasonCount >= 20;
  const formatDuration = (seconds) => {
    const total = Math.max(Number(seconds || 0), 0);
    const mins = Math.floor(total / 60);
    const secs = Math.floor(total % 60);
    return `${mins}:${String(secs).padStart(2, "0")}`;
  };
  const getStatusKey = (candidate) => {
    const eligible = canDecide(candidate);
    return candidate?.decisionStatus === "pending" && eligible
      ? "ready"
      : candidate?.decisionStatus || "pending";
  };
  const formatSummaryText = (value) => {
    if (!value) return "";
    if (typeof value !== "string") return String(value);
    const trimmed = value.trim();
    if (!trimmed) return "";
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed && typeof parsed === "object") {
        const directSummary =
          parsed.summary ||
          parsed.overallSummary ||
          parsed.finalSummary ||
          parsed.overall ||
          parsed.text ||
          parsed.analysis;
        if (typeof directSummary === "string" && directSummary.trim()) {
          return directSummary.trim();
        }
        return Object.entries(parsed)
          .map(([key, entry]) => {
            const label =
              key
                .replace(/([a-z])([A-Z])/g, "$1 $2")
                .replace(/_/g, " ")
                .replace(/\b\w/g, (char) => char.toUpperCase()) || key;
            if (Array.isArray(entry)) {
              return `${label}: ${entry.join(", ")}`;
            }
            if (entry && typeof entry === "object") {
              return `${label}: ${JSON.stringify(entry)}`;
            }
            return `${label}: ${entry}`;
          })
          .join("\n");
      }
    } catch {
      return trimmed;
    }
    return trimmed;
  };
  const sourceBadge = (value) => {
    const sourceKey = String(value || "other").toLowerCase();
    const map = {
      direct: { label: "Direct", bg: "rgba(37,99,235,.12)", color: "#1d4ed8", border: "rgba(37,99,235,.35)" },
      indeed: { label: "Indeed", bg: "rgba(124,58,237,.12)", color: "#6d28d9", border: "rgba(124,58,237,.35)" },
      ziprecruiter: { label: "ZipRecruiter", bg: "rgba(22,163,74,.12)", color: "#15803d", border: "rgba(22,163,74,.35)" },
      linkedin: { label: "LinkedIn", bg: "rgba(30,58,138,.12)", color: "#1e3a8a", border: "rgba(30,58,138,.35)" },
      other: { label: "Other", bg: "rgba(107,114,128,.12)", color: "#4b5563", border: "rgba(107,114,128,.35)" },
    };
    return map[sourceKey] || map.other;
  };
  const getTranscriptEntries = (entries, transcriptText, interviewQA) => {
    const introPatterns = [
      /hello,?\s+my name is kathy/i,
      /hello,?\s+and thank you for joining me today/i,
      /thank you for joining me today/i,
      /guiding you through your interview/i,
      /would you like to conduct this interview/i,
      /would you prefer to conduct this interview in english or spanish/i,
      /\benglish or spanish\b/i,
      /are you interviewing for a caregiver,?\s+nurse,?\s+or staff position/i,
      /first, i would like to explain how this interview will work/i,
      /first, i would like to tell you how this video interview will work/i,
      /video interview will work/i,
      /at the completion of the interview/i,
      /i may have to refer you to the hiring team/i,
      /ok,?\s+lets get started/i,
      /ok,?\s+let us get started/i,
    ];

    const normalizeText = (value) => String(value || "").trim();
    const isIntroText = (value) => {
      const text = normalizeText(value).toLowerCase();
      if (!text) return false;
      return introPatterns.some((pattern) => pattern.test(text));
    };
    const isRealStartQuestion = (value) => {
      const text = normalizeText(value).toLowerCase();
      if (!text) return false;
      return (
        text.includes("can we begin") ||
        text.includes("please introduce yourself") ||
        text.includes("share your caregiving experience")
      );
    };
    const looksLikeQuestion = (value) => {
      const text = normalizeText(value);
      if (!text) return false;
      return (
        /\?/.test(text) ||
        /^(can|could|would|please|tell me|what|why|how|when|where)\b/i.test(
          text,
        )
      );
    };

    const filterToInterviewStart = (raw) => {
      if (!Array.isArray(raw) || !raw.length) return [];
      const cleaned = raw
        .map((entry) => ({
          questionId: entry.questionId,
          questionText: normalizeText(entry.questionText),
          text: normalizeText(entry.text),
        }))
        .filter((entry) => entry.questionText || entry.text);

      let started = false;
      const result = [];
      cleaned.forEach((entry) => {
        const q = entry.questionText;
        const a = entry.text;

        if (!started) {
          // Skip anything that is clearly intro / language-selection.
          if (isIntroText(q) || isIntroText(a)) return;

          // Start when we hit the real first question (preferred),
          // or when we see a normal-looking interview question.
          if (isRealStartQuestion(q) || (q && looksLikeQuestion(q))) {
            started = true;
          } else {
            // If older transcripts don't preserve question text well,
            // fall back to starting when we see non-intro content.
            if (q || a) started = true;
          }
        }

        if (started) {
          // Drop empty / pure-intro lines after we started.
          if (isIntroText(q) && !a) return;
          result.push(entry);
        }
      });

      return result;
    };

    if (Array.isArray(interviewQA) && interviewQA.length) {
      return interviewQA
        .map((entry, index) => ({
          questionId: index + 1,
          questionText: normalizeText(entry?.question),
          text: normalizeText(entry?.answer),
        }))
        .filter((entry) => entry.questionText || entry.text);
    }
    if (Array.isArray(entries) && entries.length) {
      return filterToInterviewStart(entries);
    }
    return [];
  };
  const getQuestionMap = (questions) => {
    if (!Array.isArray(questions)) return new Map();
    return new Map(
      questions
        .filter((item) => typeof item?.id === "number" && item?.text)
        .map((item) => [item.id, item.text]),
    );
  };

  const handleDecision = async (candidate, decision) => {
    if (!candidate?._id || !locationId) return;
    if (!canDecide(candidate)) {
      window.alert(
        "This candidate has not completed all interview stages yet.",
      );
      return;
    }
    const actionLabel =
      decision === "approved"
        ? "hire"
        : decision === "holding"
          ? "hold"
          : "pass on";
    const ok = window.confirm(
      `Are you sure you want to ${actionLabel} ${candidate.name}?`,
    );
    if (!ok) return;
    try {
      setDecisionLoading(`${candidate._id}:${decision}`);
      const response = await updateAiHiringDecision(
        locationId,
        candidate._id,
        decision,
      );
      const updated = response?.candidate || response;
      setItems((prev) =>
        prev.map((item) =>
          item._id === candidate._id
            ? {
                ...item,
                decisionStatus: updated.decisionStatus,
                decisionAt: updated.decisionAt,
              }
            : item,
        ),
      );
      if (selected?._id === candidate._id) {
        setSelected((prev) => ({
          ...prev,
          decisionStatus: updated.decisionStatus,
          decisionAt: updated.decisionAt,
        }));
      }
    } catch (err) {
      window.alert(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to update decision.",
      );
    } finally {
      setDecisionLoading(null);
    }
  };

  const confirmDeleteCandidate = async () => {
    if (!deleteTarget?._id || !locationId) return;
    try {
      setDeleteLoading(true);
      await api.delete(`/ai-hiring/candidates/${deleteTarget._id}`, {
        params: { locationId },
      });
      setItems((prev) => prev.filter((item) => item._id !== deleteTarget._id));
      if (selected?._id === deleteTarget._id) {
        setSelected(null);
      }
      setDeleteTarget(null);
    } catch (err) {
      window.alert(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to delete candidate.",
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  const loadRecordingForCandidate = async (candidate) => {
    if (!candidate?._id || !locationId) return;
    try {
      setRecordingLoading(true);
      const payload = await getCandidateRecording(candidate._id, locationId);
      setRecordingData(payload || null);
    } catch {
      setRecordingData(null);
    } finally {
      setRecordingLoading(false);
    }
  };

  // Shared by both the table row "Play" button and the candidate-details
  // modal's video section, so there is exactly one place that knows how to
  // fetch + open a recording.
  const openVideoRecording = async (candidate) => {
    if (!candidate?._id || !locationId) return;
    try {
      setRowRecordingLoading(candidate._id);
      const latest = await getCandidateRecording(candidate._id, locationId);
      if (latest?.videoUrl) {
        setRecordingData(latest);
        setRecordingModalOpen(true);
      } else {
        window.alert(
          latest?.deleted
            ? "This recording has expired and is no longer available."
            : "No video recording is available for this candidate yet.",
        );
      }
    } catch (err) {
      window.alert(
        err?.response?.data?.message || "Failed to load the video recording.",
      );
    } finally {
      setRowRecordingLoading(null);
    }
  };

  const openAudioRecording = async (candidate) => {
    if (!candidate?._id || !locationId) return;
    try {
      setRowRecordingLoading(candidate._id);
      const latest = await getCandidatePhoneRecording(candidate._id, locationId);
      if (latest?.audioUrl) {
        setAudioRecordingData(latest);
        setAudioModalOpen(true);
      } else {
        window.alert(
          latest?.message ||
            "No phone interview recording is available for this candidate yet.",
        );
      }
    } catch (err) {
      window.alert(
        err?.response?.data?.message || "Failed to load the phone recording.",
      );
    } finally {
      setRowRecordingLoading(null);
    }
  };

  const handleUnlockMore = async () => {
    if (!locationId || !jobMeta?._id || !showMoreReasonReady) return;
    try {
      setShowMoreLoading(true);
      setShowMoreError("");
      await unlockMoreAiHiringCandidates({
        locationId,
        jobId: jobMeta._id,
        reason: showMoreReason.trim(),
      });
      const refreshed = await fetchAiHiringCandidates({
        locationId,
        status,
        source,
        search,
        page,
        limit,
        jobId: jobMeta._id,
      });
      setItems(Array.isArray(refreshed?.items) ? refreshed.items : []);
      setJobMeta(refreshed?.job || jobMeta);
      setShowMoreReason("");
    } catch (err) {
      setShowMoreError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to unlock additional candidates.",
      );
    } finally {
      setShowMoreLoading(false);
    }
  };

  return (
    <div className="page-shell ai-hiring-page">
      <PageHeader
        title="AI Hiring - My Candidates"
        subtitle="Review candidates linked to your agency."
        icon={
          <span role="img" aria-label="Candidates">
            👥
          </span>
        }
        {...props}
      />

      <div className="ai-hiring-content">
        {error && <div className="ai-alert ai-alert-error">{error}</div>}
        {!locationId ? (
          <div className="ai-empty">Select a location to view candidates.</div>
        ) : (
          <div className="ai-card">
            <div className="ai-card-header">
              <h3 className="ai-card-title">My Candidates</h3>
            </div>
            <div className="ai-filter-row">
              <input
                className="ai-input"
                type="text"
                placeholder="Search by name or email"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
              />
              <select
                className="ai-select"
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value);
                  setPage(1);
                }}
              >
                <option value="all">All</option>
                <option value="approved">Hired</option>
                <option value="pending">Resume Review</option>
                <option value="rejected">Passed</option>
                <option value="follow_up">Phone Interview</option>
                {VIDEO_INTERVIEW_FEATURE_ENABLED ? (
                  <option value="follow_up_video">Video Interview</option>
                ) : null}
                <option value="phone_passed_awaiting_review">
                  Final Review
                </option>
                <option value="holding">Held</option>
              </select>
              <select
                className="ai-select"
                value={source}
                onChange={(e) => {
                  setSource(e.target.value);
                  setPage(1);
                }}
              >
                <option value="all">All Sources</option>
                <option value="direct">Direct</option>
                <option value="indeed">Indeed</option>
                <option value="ziprecruiter">ZipRecruiter</option>
                <option value="linkedin">LinkedIn</option>
              </select>
              <select
                className="ai-select"
                value={sortOption}
                onChange={(e) => {
                  setSortOption(e.target.value);
                  setPage(1);
                }}
                aria-label="Sort candidates by"
              >
                {SORT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    Sort: {opt.label}
                  </option>
                ))}
              </select>
            </div>
            {loading ? (
              <div className="ai-empty">Loading candidates...</div>
            ) : items.length ? (
              <div className="ai-table-wrapper">
                {showingMore && jobMeta?.showMoreUsedAt ? (
                  <div className="ai-muted ai-extended-notice">
                    Extended list — additional candidates unlocked on{" "}
                    {new Date(jobMeta.showMoreUsedAt).toLocaleDateString()}
                  </div>
                ) : null}
                <table className="ai-table ai-table-candidates">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Source</th>
                      <th>Rating</th>
                      <th>Stage</th>
                      <th>Recording</th>
                      <th>Date</th>
                      <th>Decision</th>
                      <th>Details</th>
                      <th>Delete</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleCandidates.map((item) => {
                      const eligible = canDecide(item);
                      const decisionDisabled =
                        decisionLoading && decisionLoading.startsWith(item._id);
                      return (
                        <tr key={item._id}>
                          <td>{item.name}</td>
                          <td>
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                padding: "4px 10px",
                                borderRadius: 999,
                                fontSize: 11,
                                fontWeight: 700,
                                background: sourceBadge(item.source).bg,
                                color: sourceBadge(item.source).color,
                                border: `1px solid ${sourceBadge(item.source).border}`,
                              }}
                            >
                              {sourceBadge(item.source).label}
                            </span>
                          </td>
                          <td>
                            {hasCriteriaChecklist(item) ? (
                              <CriteriaBadge
                                metCount={item.criteriaMetCount}
                                totalCount={item.criteriaTotalCount}
                              />
                            ) : (
                              <ScoreBadge score={item.aiScore?.overallScore || 0} />
                            )}
                          </td>
                          <td>
                            {(() => {
                              const stage = getStageInfo(item);
                              return (
                                <>
                                  <span className={stageClassName(stage.key)}>
                                    {stage.label}
                                  </span>
                                  {item.unresponsive ? (
                                    <div style={{ marginTop: 6 }}>
                                      <span className="ai-tag ai-tag--warning">
                                        📨 In Drip
                                      </span>
                                    </div>
                                  ) : null}
                                  {VIDEO_CONSENT_DECLINE_LABELS[item.videoConsentStatus] ? (
                                    <div style={{ marginTop: 6 }}>
                                      <VideoConsentDeclineBadge
                                        videoConsentStatus={item.videoConsentStatus}
                                      />
                                    </div>
                                  ) : null}
                                  {VIDEO_ENDED_BY_LABELS[item.videoInterview?.endedBy] ? (
                                    <div style={{ marginTop: 6 }}>
                                      <VideoEndedByBadge endedBy={item.videoInterview?.endedBy} />
                                    </div>
                                  ) : null}
                                </>
                              );
                            })()}
                          </td>
                          <td>
                            {(() => {
                              const rowLoading = rowRecordingLoading === item._id;
                              if (isVideoPathFor(item)) {
                                if (!hasVideoEvidence(item)) {
                                  return <span className="ai-muted">Not yet available</span>;
                                }
                                return (
                                  <button
                                    type="button"
                                    className="ai-button ai-button-sm ai-button-primary"
                                    disabled={rowLoading}
                                    onClick={() => openVideoRecording(item)}
                                  >
                                    {rowLoading ? "Loading..." : "▶ Video"}
                                  </button>
                                );
                              }
                              if (hasPhoneEvidence(item)) {
                                return (
                                  <button
                                    type="button"
                                    className="ai-button ai-button-sm ai-button-primary"
                                    disabled={rowLoading}
                                    onClick={() => openAudioRecording(item)}
                                  >
                                    {rowLoading ? "Loading..." : "▶ Audio"}
                                  </button>
                                );
                              }
                              return <span className="ai-muted">Not yet available</span>;
                            })()}
                          </td>
                          <td>
                            {item.createdAt
                              ? formatYyyyDdMm(item.createdAt)
                              : "--"}
                          </td>
                          <td>
                            {eligible ? (
                              <div className="ai-decision-actions">
                                <button
                                  type="button"
                                  className="ai-button ai-button-sm ai-button-success"
                                  onClick={() =>
                                    handleDecision(item, "approved")
                                  }
                                  disabled={
                                    decisionDisabled ||
                                    item.decisionStatus === "approved"
                                  }
                                >
                                  {item.decisionStatus === "approved"
                                    ? "Hired"
                                    : decisionLoading === `${item._id}:approved`
                                      ? "Updating..."
                                      : "Hire"}
                                </button>
                                <button
                                  type="button"
                                  className="ai-button ai-button-sm ai-button-warning"
                                  onClick={() =>
                                    handleDecision(item, "holding")
                                  }
                                  disabled={
                                    decisionDisabled ||
                                    item.decisionStatus === "holding"
                                  }
                                >
                                  {item.decisionStatus === "holding"
                                    ? "Held"
                                    : decisionLoading === `${item._id}:holding`
                                      ? "Updating..."
                                      : "Hold"}
                                </button>
                                <button
                                  type="button"
                                  className="ai-button ai-button-sm ai-button-danger"
                                  onClick={() =>
                                    handleDecision(item, "rejected")
                                  }
                                  disabled={
                                    decisionDisabled ||
                                    item.decisionStatus === "rejected"
                                  }
                                >
                                  {item.decisionStatus === "rejected"
                                    ? "Passed"
                                    : decisionLoading === `${item._id}:rejected`
                                      ? "Updating..."
                                      : "Pass"}
                                </button>
                              </div>
                            ) : (
                              <span className="ai-muted">
                                Awaiting interviews
                              </span>
                            )}
                          </td>
                          <td>
                            <button
                              type="button"
                              className="ai-link-button"
                              onClick={() => setSelected(item)}
                            >
                              View details
                            </button>
                          </td>
                          <td>
                            <button
                              type="button"
                              className="ai-icon-button ai-icon-button--danger"
                              onClick={() => setDeleteTarget(item)}
                              aria-label="Delete candidate"
                              title="Delete candidate"
                            >
                              🗑
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="ai-empty">No candidates found.</div>
            )}
            {canShowMore ? (
              <div className="ai-show-more-card">
                <h4 className="ai-show-more-title">Need to see more candidates?</h4>
                <p className="ai-show-more-subtitle">
                  Please explain why none of the current candidates met your
                  requirements.
                </p>
                <textarea
                  className="ai-textarea ai-show-more-textarea"
                  rows={4}
                  value={showMoreReason}
                  onChange={(e) => {
                    setShowMoreReason(e.target.value);
                    if (showMoreError) setShowMoreError("");
                  }}
                  placeholder="E.g. All candidates lacked the required certification..."
                />
                <div
                  className={`ai-show-more-count ${
                    showMoreReasonReady ? "ai-show-more-count--ready" : ""
                  }`}
                >
                  Characters: {showMoreReasonCount}/20
                </div>
                <button
                  type="button"
                  className="ai-button ai-button-primary"
                  disabled={!showMoreReasonReady || showMoreLoading}
                  onClick={handleUnlockMore}
                >
                  {showMoreLoading ? "Unlocking..." : "Unlock More Candidates"}
                </button>
                {showMoreError ? (
                  <div className="ai-show-more-error">{showMoreError}</div>
                ) : null}
              </div>
            ) : null}
          </div>
        )}
      </div>

      {selected && (
        <div
          className="ai-modal-overlay"
          role="dialog"
          aria-modal="true"
          onClick={() => setSelected(null)}
        >
          <div className="ai-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ai-modal-header">
              <div>
                <h3 className="ai-card-title">{selected.name}</h3>
                <p className="ai-modal-subtitle">{selected.email}</p>
              </div>
              <button
                type="button"
                className="ai-close-button"
                onClick={() => setSelected(null)}
              >
                ✕
              </button>
            </div>
            <div className="ai-modal-body">
              <div className="ai-detail-grid">
                <div>
                  <div className="ai-detail-label">
                    {hasCriteriaChecklist(selected) ? "Evaluation Criteria" : "AI Score"}
                  </div>
                  <div className="ai-detail-value">
                    {hasCriteriaChecklist(selected) ? (
                      <CriteriaBadge
                        metCount={selected.criteriaMetCount}
                        totalCount={selected.criteriaTotalCount}
                      />
                    ) : (
                      <ScoreBadge score={selected.aiScore?.overallScore || 0} />
                    )}
                  </div>
                  {VIDEO_CONSENT_DECLINE_LABELS[selected.videoConsentStatus] ? (
                    <div style={{ marginTop: 6 }}>
                      <VideoConsentDeclineBadge
                        videoConsentStatus={selected.videoConsentStatus}
                      />
                    </div>
                  ) : null}
                  {VIDEO_ENDED_BY_LABELS[selected.videoInterview?.endedBy] ? (
                    <div style={{ marginTop: 6 }}>
                      <VideoEndedByBadge endedBy={selected.videoInterview?.endedBy} />
                    </div>
                  ) : null}
                  <div className="ai-muted" style={{ marginTop: 4 }}>
                    {hasCriteriaChecklist(selected)
                      ? "How this candidate stacks up against the job's checklist — see details below."
                      : "Composite recommendation based on application and interview stages."}
                  </div>
                </div>
                <div>
                  <div className="ai-detail-label">Phone</div>
                  <div className="ai-detail-value">
                    {selected.phone || "--"}
                  </div>
                </div>
                <div>
                  <div className="ai-detail-label">Email</div>
                  <div className="ai-detail-value">
                    {selected.email || "--"}
                  </div>
                </div>
                <div>
                  <div className="ai-detail-label">Role</div>
                  <div className="ai-detail-value">
                    {normalizeRole(selected.role)}
                  </div>
                </div>
                <div>
                  <div className="ai-detail-label">Address</div>
                  <div className="ai-detail-value">
                    {selected.location || "--"}
                  </div>
                </div>
                <div>
                  <div className="ai-detail-label">Status</div>
                  <div
                    className={
                      statusConfig[getStatusKey(selected)]?.className ||
                      "ai-tag ai-tag--neutral"
                    }
                  >
                    {statusConfig[getStatusKey(selected)]?.label}
                  </div>
                </div>
                <div>
                  <div className="ai-detail-label">Applied Date</div>
                  <div className="ai-detail-value">
                    {selected.appliedDate || "--"}
                  </div>
                </div>
                <div>
                  <div className="ai-detail-label">Created</div>
                  <div className="ai-detail-value">
                    {selected.createdAt
                      ? new Date(selected.createdAt).toLocaleString()
                      : "--"}
                  </div>
                </div>
                <div>
                  <div className="ai-detail-label">Resume</div>
                  <div className="ai-detail-value">
                    {selected.resumeFile?.name ? (
                      <button
                        type="button"
                        className="ai-link-button"
                        onClick={handleDownloadResume}
                        disabled={downloading}
                      >
                        {downloading ? "Downloading..." : "Download resume"}
                      </button>
                    ) : selected.resumeUrl ? (
                      <a
                        href={selected.resumeUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="ai-link-button"
                      >
                        Open resume link
                      </a>
                    ) : (
                      "Not provided"
                    )}
                  </div>
                </div>
              </div>

              {hasCriteriaChecklist(selected) ? (
                <div>
                  <div className="ai-section-title">Checklist Details</div>
                  <CriteriaChecklist criteriaResults={selected.criteriaResults} />
                </div>
              ) : (
                <div>
                  <div className="ai-section-title">AI Summary</div>
                  <div className="ai-detail-text">
                    {selected.aiScore?.summary ||
                      "AI summary will appear after scoring completes."}
                  </div>
                </div>
              )}

              <div>
                <div className="ai-section-title">Strengths</div>
                <div className="ai-chip-row">
                  {(selected.strengths || selected.aiScore?.strengths || [])
                    .length ? (
                    (
                      selected.strengths ||
                      selected.aiScore?.strengths ||
                      []
                    ).map((item) => (
                      <span key={item} className="ai-chip ai-chip--good">
                        {item}
                      </span>
                    ))
                  ) : (
                    <span className="ai-muted">
                      {selected.phoneInterviewHasTranscript
                        ? "No strengths yet."
                        : "Pending interview completion"}
                    </span>
                  )}
                </div>
              </div>

              <div>
                <div className="ai-section-title">Weaknesses</div>
                <div className="ai-chip-row">
                  {(selected.weaknesses || selected.aiScore?.weaknesses || [])
                    .length ? (
                    (
                      selected.weaknesses ||
                      selected.aiScore?.weaknesses ||
                      []
                    ).map((item) => (
                      <span key={item} className="ai-chip ai-chip--bad">
                        {item}
                      </span>
                    ))
                  ) : (
                    <span className="ai-muted">
                      {selected.phoneInterviewHasTranscript
                        ? "No weaknesses yet."
                        : "Pending interview completion"}
                    </span>
                  )}
                </div>
              </div>

              {/* Phone and video are mutually exclusive per candidate — show
                  whichever one actually applies (real call evidence always
                  wins over the dashboard-rollout flag; the flag only gates
                  the pre-interview guess), never both/never Phone
                  unconditionally regardless of what the candidate took. */}
              {!isVideoPathFor(selected) ? (
              <>
              <div>
                <div className="ai-section-title">Phone Interview Summary</div>
                <div className="ai-detail-text">
                  <strong>Phone Rating:</strong>{" "}
                  <ScoreBadge score={selected.phoneInterview?.score || 0} />
                </div>
                <div className="ai-detail-text ai-detail-text--pre">
                  {formatSummaryText(selected.phoneInterviewSummary) ||
                    "Phone interview summary will appear after completion."}
                </div>
              </div>

              <div>
                <div className="ai-section-title">Phone Interview Details</div>
                <p className="ai-muted" style={{ marginBottom: 12 }}>
                  Full conversation between Albert AI and the applicant.
                </p>
                <PhoneInterviewDetailsSection candidate={selected} />
              </div>
              </>
              ) : null}

              {isVideoPathFor(selected) ? (
              <div>
                <div className="ai-section-title">Video Interview Summary</div>
                <div className="ai-detail-text">
                  <strong>Video Rating:</strong>{" "}
                  <ScoreBadge score={selected.videoInterview?.score || 0} />
                </div>
                <div className="ai-detail-text ai-detail-text--pre">
                  {formatSummaryText(selected.videoInterviewSummary) ||
                    "Video interview summary will appear after completion."}
                </div>
                {selected.videoInterviewTranscript ||
                selected.videoInterviewTranscriptEntries?.length ? (
                  <div style={{ display: "grid", gap: 8 }}>
                    {getTranscriptEntries(
                      selected.videoInterviewTranscriptEntries,
                      selected.videoInterviewTranscript,
                      selected.videoInterviewQA,
                    ).flatMap((entry, index) => {
                      const questionText = getQuestionMap(
                        selected.videoInterviewQuestions,
                      ).get(entry.questionId);
                      return [
                        <TranscriptMessageBubble
                          key={`video-${entry.questionId || index}-q`}
                          role="Kathy"
                          message={questionText || entry.questionText || "Question"}
                        />,
                        <TranscriptMessageBubble
                          key={`video-${entry.questionId || index}-a`}
                          role="Candidate"
                          message={entry.text}
                        />,
                      ];
                    })}
                  </div>
                ) : (
                  <div className="ai-muted">Transcript not available.</div>
                )}
              </div>
              ) : null}

              {isVideoPathFor(selected) ? (
                <div>
                  <div className="ai-section-title">Video Interview</div>
                  {recordingLoading ? (
                    <div className="ai-muted">Loading recording...</div>
                  ) : recordingData?.screenshotUrl ? (
                    <div className="ai-recording-section">
                      <img
                        src={recordingData.screenshotUrl}
                        alt="Video interview preview"
                        className="ai-recording-thumbnail"
                      />
                      <div className="ai-recording-meta">
                        <div className="ai-detail-value">
                          {recordingData.deleted
                            ? "Recording expired"
                            : "Interview recording available"}
                        </div>
                        <div className="ai-muted">
                          Duration: {formatDuration(recordingData.durationSec)}
                        </div>
                        {recordingData.uploadedAt ? (
                          <div className="ai-muted">
                            Recorded:{" "}
                            {new Date(recordingData.uploadedAt).toLocaleString()}
                          </div>
                        ) : null}
                        {!recordingData.deleted ? (
                          <button
                            type="button"
                            className="ai-button ai-button-sm ai-button-primary"
                            onClick={() => openVideoRecording(selected)}
                          >
                            ▶ Play Recording
                          </button>
                        ) : (
                          <span className="ai-tag ai-tag--neutral">
                            Recording expired
                          </span>
                        )}
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
            <div className="ai-modal-actions">
              <button
                type="button"
                className="ai-button ai-button-primary"
                onClick={() => setSelected(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div
          className="ai-modal-overlay"
          role="dialog"
          aria-modal="true"
          onClick={() => setDeleteTarget(null)}
        >
          <div className="ai-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ai-modal-header">
              <div>
                <h3 className="ai-card-title">Delete candidate</h3>
                <p className="ai-modal-subtitle">
                  Are you sure you want to delete {deleteTarget.name}?
                </p>
              </div>
              <button
                type="button"
                className="ai-close-button"
                onClick={() => setDeleteTarget(null)}
              >
                ✕
              </button>
            </div>
            <div className="ai-modal-actions">
              <button
                type="button"
                className="ai-button ai-button-secondary"
                onClick={() => setDeleteTarget(null)}
                disabled={deleteLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="ai-button ai-button-danger"
                onClick={confirmDeleteCandidate}
                disabled={deleteLoading}
              >
                {deleteLoading ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
      {recordingModalOpen && recordingData?.videoUrl ? (
        <div
          className="ai-modal-overlay"
          role="dialog"
          aria-modal="true"
          onClick={() => setRecordingModalOpen(false)}
        >
          <div className="ai-modal ai-modal-recording" onClick={(e) => e.stopPropagation()}>
            <div className="ai-modal-header">
              <div>
                <h3 className="ai-card-title">Candidate Recording</h3>
              </div>
              <button
                type="button"
                className="ai-close-button"
                onClick={() => setRecordingModalOpen(false)}
              >
                ✕
              </button>
            </div>
            <div className="ai-modal-body">
              <video
                src={recordingData.videoUrl}
                controls
                autoPlay
                style={{ maxWidth: "100%", width: "100%" }}
              />
            </div>
          </div>
        </div>
      ) : null}
      {audioModalOpen && audioRecordingData?.audioUrl ? (
        <div
          className="ai-modal-overlay"
          role="dialog"
          aria-modal="true"
          onClick={() => setAudioModalOpen(false)}
        >
          <div className="ai-modal ai-modal-recording" onClick={(e) => e.stopPropagation()}>
            <div className="ai-modal-header">
              <div>
                <h3 className="ai-card-title">Phone Interview Recording</h3>
              </div>
              <button
                type="button"
                className="ai-close-button"
                onClick={() => setAudioModalOpen(false)}
              >
                ✕
              </button>
            </div>
            <div className="ai-modal-body">
              <audio
                src={audioRecordingData.audioUrl}
                controls
                autoPlay
                style={{ width: "100%" }}
              />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default AiHiringCandidates;
